import { useState } from "react";
import { NavLink } from "react-router-dom";
import { CheckCircle, ArrowLeft, Lock } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useCart } from "@/lib/cartContext";

function Field({
  label, placeholder, type = "text", className = "",
}: { label: string; placeholder: string; type?: string; className?: string }) {
  return (
    <div className={className}>
      <label className="block text-[10px] tracking-widest uppercase text-stone-500 font-medium mb-2">{label}</label>
      <input
        type={type}
        placeholder={placeholder}
        className="w-full border border-stone-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-stone-900 placeholder-stone-300 text-stone-800 transition-colors"
      />
    </div>
  );
}

export default function Checkout() {
  const { items, total, clearCart, itemCount } = useCart();
  const [step, setStep] = useState<"form" | "success">("form");

  const shipping = total >= 150 ? 0 : 14;
  const grandTotal = total + shipping;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStep("success");
    clearCart();
  }

  if (step === "success") {
    const orderNum = `AUR-${Math.floor(Math.random() * 90000 + 10000)}`;
    return (
      <div className="min-h-screen flex flex-col bg-stone-50">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center gap-8 px-4 text-center">
          <div className="size-20 border-2 border-green-500 rounded-full flex items-center justify-center">
            <CheckCircle className="size-10 text-green-500" />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-emerald-600 font-semibold mb-3">Ordine confermato</p>
            <h2 className="font-semibold text-2xl sm:text-3xl md:text-4xl md:font-bold text-stone-900 tracking-tight mb-4">Grazie per il tuo acquisto</h2>
            <p className="text-stone-500 max-w-sm leading-relaxed">
              Riceverai una email di conferma con i dettagli di spedizione. Il tuo ordine verrà preparato con cura nei nostri atelier.
            </p>
          </div>
          <p className="text-xs text-stone-400">
            Numero ordine: <strong className="text-stone-700 font-medium">{orderNum}</strong>
          </p>
          <NavLink
            to="/shop"
            className="text-[10px] tracking-widest uppercase bg-stone-900 text-white px-10 py-4 hover:bg-stone-700 transition-colors mt-4"
          >
            Continua lo shopping
          </NavLink>
        </main>
        <Footer />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-stone-50">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center gap-4">
          <p className="text-stone-500 font-medium text-lg sm:text-xl md:text-2xl md:font-semibold tracking-tight">La selezione è vuota.</p>
          <NavLink
            to="/shop"
            className="text-[10px] tracking-widest uppercase bg-stone-900 text-white px-8 py-3 hover:bg-stone-700 transition-colors"
          >
            Vai alla collezione
          </NavLink>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <Navbar />
      <main className="flex-1">
        <div className="bg-white border-b border-stone-100 py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <NavLink to="/cart" className="text-stone-400 hover:text-stone-900 transition-colors p-1">
                <ArrowLeft className="size-4" />
              </NavLink>
              <div className="text-center flex-1">
                <p className="text-[10px] tracking-[0.3em] uppercase text-emerald-600 font-semibold mb-1">Brupa</p>
                <h1 className="font-semibold text-xl sm:text-2xl md:text-3xl md:font-bold text-stone-900 tracking-tight">Conferma ordine</h1>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {/* Form */}
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-white p-8">
                  <h2 className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-semibold mb-6">Dati personali</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Nome" placeholder="Giulia" />
                    <Field label="Cognome" placeholder="Rossi" />
                    <Field label="Email" placeholder="giulia@email.it" type="email" className="sm:col-span-2" />
                    <Field label="Telefono" placeholder="+39 333 1234567" type="tel" className="sm:col-span-2" />
                  </div>
                </div>

                <div className="bg-white p-8">
                  <h2 className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-semibold mb-6">Indirizzo di spedizione</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Via e numero" placeholder="Via della Spiga 7" className="sm:col-span-2" />
                    <Field label="Città" placeholder="Milano" />
                    <Field label="CAP" placeholder="20121" />
                    <Field label="Provincia" placeholder="MI" />
                    <Field label="Paese" placeholder="Italia" />
                  </div>
                </div>

                <div className="bg-white p-8">
                  <h2 className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-semibold mb-1 flex items-center gap-2">
                    <Lock className="size-3.5 text-stone-400" />
                    Pagamento sicuro
                  </h2>
                  <p className="text-xs text-stone-400 mb-6">Tutti i dati sono criptati con SSL 256-bit.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Numero carta" placeholder="•••• •••• •••• ••••" className="sm:col-span-2" />
                    <Field label="Intestatario" placeholder="GIULIA ROSSI" />
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Scadenza" placeholder="MM/AA" />
                      <Field label="CVV" placeholder="•••" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white p-8 sticky top-24">
                  <h2 className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-semibold mb-6">Riepilogo</h2>

                  <div className="space-y-4 mb-6">
                    {items.map(({ product, quantity }) => (
                      <div key={product.id} className="flex gap-3">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-14 h-18 object-cover bg-stone-100 shrink-0"
                          style={{ height: "72px" }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-stone-800 truncate">{product.name}</p>
                          <p className="text-[10px] text-stone-400 mt-0.5 capitalize">{product.category} · ×{quantity}</p>
                          <p className="text-xs font-semibold text-stone-800 mt-1">
                            €{(product.price * quantity).toLocaleString("it-IT")}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-stone-100 pt-5 space-y-3 text-sm mb-8">
                    <div className="flex justify-between text-stone-500">
                      <span>Subtotale</span><span>€{total.toLocaleString("it-IT")}</span>
                    </div>
                    <div className="flex justify-between text-stone-500">
                      <span>Spedizione</span>
                      <span className={shipping === 0 ? "text-green-600 font-medium" : ""}>
                        {shipping === 0 ? "Gratuita" : `€${shipping.toFixed(2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-stone-900 pt-3 border-t border-stone-100">
                      <span>Totale</span><span>€{grandTotal.toLocaleString("it-IT")}</span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 bg-stone-900 text-white text-[10px] tracking-widest uppercase font-semibold flex items-center justify-center gap-2 hover:bg-stone-700 transition-colors"
                  >
                    <Lock className="size-3.5" />
                    Conferma ordine
                  </button>
                  <p className="text-[10px] text-stone-400 text-center mt-3">
                    Pagamento sicuro SSL · Politica di reso 30 giorni
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}
